/**
 * @file SearchInput.tsx
 * @description Поле поиска для строки фильтров (phase19.23 этап A).
 *
 * Домен-нейтральное: иконка, крестик очистки и тач-цель одинаковы во всех
 * списках админки.
 *
 * phase19.31 (зона «Склад», SC43, SC44): высота — из темы (`--control-h`:
 * 48 до 1024, 44 с 1024, 56 у кладовщика до 1024), скругление 8px и шрифт
 * поля — как у остальных полей. `size="wms"` оставлен для совместимости и
 * ничего не меняет: крупный размер даёт плотность каркаса по роли, а не
 * страница.
 *
 * ```tsx
 * <SearchInput
 *   value={search}
 *   onChange={setSearch}
 *   placeholder="Номер, склад или сотрудник"
 *   ariaLabel="Поиск по списаниям"
 * />
 * ```
 */

import { Search, X } from "lucide-react";

import { cn } from "@/lib/utils";

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel: string;
  /** @deprecated Размер даёт тема (плотность каркаса), проп не влияет. */
  size?: "default" | "wms";
  className?: string;
}

export function SearchInput({ value, onChange, placeholder, ariaLabel, className }: Props) {
  return (
    <div
      data-slot="search-input"
      className={cn(
        "flex min-h-[var(--control-h,3rem)] items-center gap-2 rounded-lg bg-surface-lowest px-3",
        "[box-shadow:inset_0_0_0_1px_var(--field-border,var(--border))]",
        "focus-within:[box-shadow:inset_0_0_0_1px_var(--field-border-focus,var(--primary)),0_0_0_2px_var(--ring)]",
        className,
      )}
    >
      <Search className="size-4 shrink-0 text-on-surface-variant" aria-hidden />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        // Свой крестик очистки уже есть справа; нативный WebKit-крестик рисует
        // второй, меньше 48px и мимо DS — прячем.
        className="min-w-0 flex-1 bg-transparent text-[length:var(--fs-field,1rem)] outline-none placeholder:text-on-surface-variant [&::-webkit-search-cancel-button]:hidden"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Очистить поиск"
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-on-surface-variant hover:bg-surface-low"
        >
          <X className="size-4" />
        </button>
      ) : null}
    </div>
  );
}
